/**
 * Date Helper for antd v5 compatibility
 * Antd v5 uses dayjs for date handling
 * This helper provides date utilities using dayjs exclusively
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import weekday from 'dayjs/plugin/weekday';

// Extend dayjs with required plugins
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);

// Standard date formats used across the application
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_FORMAT = 'DD-MM-YYYY';
export const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Safe date parser - handles various date formats and returns valid dayjs object
 * @param {string|Date|dayjs} dateInput - Date input in various formats
 * @param {string} format - Expected format (optional)
 * @returns {dayjs.Dayjs|null} - Valid dayjs object or null
 */
export const parseDate = (dateInput, format = null) => {
  if (!dateInput) return null;
  
  // Already a dayjs object
  if (dayjs.isDayjs(dateInput)) {
    return dateInput.isValid() ? dateInput : null;
  }
  
  // Date object
  if (dateInput instanceof Date) {
    const parsed = dayjs(dateInput);
    return parsed.isValid() ? parsed : null;
  }
  
  // String - try parsing with format first, then without
  if (typeof dateInput === 'string') {
    let parsed;
    
    // Try with explicit format if provided
    if (format) {
      parsed = dayjs(dateInput, format, true);
      if (parsed.isValid()) return parsed;
    }
    
    // Try common formats
    const formats = [
      'YYYY-MM-DD',
      'YYYY-MM-DDTHH:mm:ss',
      'DD-MM-YYYY',
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'YYYY/MM/DD'
    ];
    
    for (const fmt of formats) {
      parsed = dayjs(dateInput, fmt, true);
      if (parsed.isValid()) return parsed;
    }
    
    // Last resort - let dayjs try to parse it
    parsed = dayjs(dateInput);
    return parsed.isValid() ? parsed : null;
  }
  
  return null;
};

/**
 * Calculate duration between two dates in days
 * @param {dayjs.Dayjs|string|Date} endDate - End date
 * @param {dayjs.Dayjs|string|Date} startDate - Start date
 * @returns {number} Duration in days
 */
export const getDurationInDays = (endDate, startDate) => {
  const end = parseDate(endDate);
  const start = parseDate(startDate);
  if (!end || !start) return 0;
  return end.diff(start, 'day');
};

/**
 * Get current date as dayjs
 */
export const today = () => dayjs();

/**
 * Get UTC date
 */
export const getUtcDate = (dateInput) => {
  const parsed = parseDate(dateInput);
  return parsed ? dayjs.utc(parsed) : dayjs.utc();
};

/**
 * Disable dates before today (for DatePicker disabledDate prop)
 */
export const disablePastDates = (current) => {
  return current && current < dayjs().startOf('day');
};

/**
 * Disable dates before a specific date
 */
export const disableDatesBefore = (referenceDate) => (current) => {
  if (!referenceDate) return false;
  const refDayjs = dayjs.isDayjs(referenceDate) ? referenceDate : dayjs(referenceDate);
  return current && current < refDayjs.startOf('day');
};

/**
 * Disable dates before or equal to a specific date
 */
export const disableDatesBeforeOrEqual = (referenceDate) => (current) => {
  if (!referenceDate) return false;
  const refDayjs = dayjs.isDayjs(referenceDate) ? referenceDate : dayjs(referenceDate);
  return current && current <= refDayjs.startOf('day');
};

/**
 * API / Tripjack free-cancel deadline.
 * Portal listing / details / checkout show that calendar day minus 1 (not check-in−1).
 *
 * Example: Tripjack deadline 27 Nov → Portal "Free Cancellation till" 26 Nov.
 */
export const HOTEL_PORTAL_CANCELLATION_BUFFER_DAYS = 1;
export const HOTEL_PORTAL_CANCELLATION_DISPLAY_OFFSET_DAYS =
  HOTEL_PORTAL_CANCELLATION_BUFFER_DAYS;

/** Written YYYY-MM-DD. Tripjack penalty clocks are already IST; a trailing Z must not roll 23:59 into the next day. */
const extractHotelCancellationCalendarYmd = (dateInput) => {
  if (dateInput == null || dateInput === '') return '';
  if (dayjs.isDayjs(dateInput) && dateInput.isValid()) {
    return dateInput.format(DATE_FORMAT);
  }
  if (typeof dateInput === 'string') {
    const text = dateInput.trim();
    const datePart = text.split(/[T\s]/)[0];
    const iso = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const dmy = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (iso) {
      return `${iso[1]}-${iso[2]}-${iso[3]}`;
    }
    if (dmy) {
      return `${dmy[3]}-${String(dmy[2]).padStart(2, '0')}-${String(dmy[1]).padStart(2, '0')}`;
    }
  }
  if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
    const y = dateInput.getUTCFullYear();
    const m = String(dateInput.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dateInput.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const parsed = parseDate(dateInput);
  if (parsed?.isValid?.()) {
    return parsed.format(DATE_FORMAT);
  }
  return '';
};

export const applyHotelPortalCancellationDisplayOffset = (dateInput) => {
  const cal = extractHotelCancellationCalendarYmd(dateInput);
  if (!cal) return null;

  // UTC calendar math — avoids IST/local shifting API−1 into the API day on Details.
  const base = dayjs.utc(cal, DATE_FORMAT, true);
  if (!base.isValid()) return null;
  return base.subtract(HOTEL_PORTAL_CANCELLATION_DISPLAY_OFFSET_DAYS, 'day');
};

/** Tripjack penalty clock, written as 04:29 AM. Date-only values and midnight stay blank. */
export const formatHotelCancellationClock = (dateInput) => {
  if (dateInput == null || dateInput === '') return '';
  const text = String(dateInput).trim();
  const match = text.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/i)
    || text.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return '';
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  const ss = Number(match[3] ?? '0');
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return '';
  if (hh === 0 && mm === 0 && ss === 0) return '';
  return dayjs().hour(hh).minute(mm).second(0).format('hh:mm A');
};

export const formatHotelPortalCancellationDisplayDate = (
  dateInput,
  format = DISPLAY_FORMAT,
) => {
  const shifted = applyHotelPortalCancellationDisplayOffset(dateInput);
  return shifted ? shifted.format(format) : '';
};

/**
 * Format a date for display
 * @param {string|Date|dayjs} dateInput - Date input
 * @param {string} format - Output format (default: DD-MM-YYYY)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, format = DISPLAY_FORMAT) => {
  const parsed = parseDate(dateInput);
  return parsed ? parsed.format(format) : '';
};

/**
 * Format a date for API requests
 * @param {string|Date|dayjs} dateInput - Date input
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForAPI = (dateInput) => {
  const parsed = parseDate(dateInput);
  return parsed ? parsed.format(DATE_FORMAT) : '';
};

/**
 * Calendar stay date only (YYYY-MM-DD). Prefer leading Y-M-D so ISO timestamps
 * like …T18:30:00.000Z do not shift +1 day in IST when counting nights.
 */
export const toStayDateOnly = (dateInput) => {
  if (dateInput == null || dateInput === "") return "";
  if (dayjs.isDayjs(dateInput) && dateInput.isValid()) {
    return dateInput.format(DATE_FORMAT);
  }
  const raw = Array.isArray(dateInput) ? dateInput[0] : dateInput;
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const parsed = parseDate(s);
  return parsed?.isValid() ? parsed.format(DATE_FORMAT) : "";
};

/** Hotel stay nights: checkout − checkin in calendar days (same as search 5N badge). */
export const getStayNights = (checkInDate, checkOutDate) => {
  const checkIn = toStayDateOnly(checkInDate);
  const checkOut = toStayDateOnly(checkOutDate);
  if (!checkIn || !checkOut) return 0;
  const nights = dayjs(checkOut)
    .startOf("day")
    .diff(dayjs(checkIn).startOf("day"), "day");
  return nights > 0 ? nights : 0;
};

/**
 * Get duration object from two dates
 * @param {dayjs.Dayjs|string|Date} endDate - End date
 * @param {dayjs.Dayjs|string|Date} startDate - Start date
 * @returns {dayjs.Duration} Duration object
 */
export const getDuration = (endDate, startDate) => {
  const end = parseDate(endDate);
  const start = parseDate(startDate);
  if (!end || !start) return null;
  return dayjs.duration(end.diff(start));
};

export default {
  parseDate,
  today,
  getUtcDate,
  disablePastDates,
  disableDatesBefore,
  disableDatesBeforeOrEqual,
  formatDate,
  formatDateForAPI,
  formatHotelPortalCancellationDisplayDate,
  formatHotelCancellationClock,
  applyHotelPortalCancellationDisplayOffset,
  HOTEL_PORTAL_CANCELLATION_BUFFER_DAYS,
  HOTEL_PORTAL_CANCELLATION_DISPLAY_OFFSET_DAYS,
  getDurationInDays,
  getDuration,
  getStayNights,
  toStayDateOnly,
  DATE_FORMAT,
  DISPLAY_FORMAT,
  DATETIME_FORMAT
};
